import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { User, Referral, Commission, KPIData, ChartData, GeographicData, CommissionStatus } from '../types';

export class DataService {
  private static readonly USERS_COLLECTION = 'users';
  private static readonly REFERRALS_COLLECTION = 'referrals';
  private static readonly PROPERTIES_COLLECTION = 'properties';

  /**
   * Test method to check if we can access Firestore
   */
  static async testFirestoreAccess(): Promise<boolean> {
    try {
      console.log('🧪 Testing Firestore access...');
      const testDoc = await getDoc(doc(db, 'test', 'test'));
      console.log('✅ Firestore access test successful');
      return true;
    } catch (error) {
      console.error('❌ Firestore access test failed:', error);
      return false;
    }
  }

  /**
   * Get all users with their roles
   */
  static async getUsers(): Promise<User[]> {
    try {
      console.log('🔍 Fetching users from collection:', DataService.USERS_COLLECTION);
      const usersSnapshot = await getDocs(collection(db, DataService.USERS_COLLECTION));
      console.log('📊 Users snapshot size:', usersSnapshot.size);
      
      const users: User[] = [];
      
      usersSnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('👤 User data:', doc.id, data);
        users.push({
          id: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          phone: data.phone || '',
          role: data.role || 'recommender',
          createdAt: data.createdAt?.toDate() || new Date(),
          isActive: data.isActive !== false,
          status: data.status || 'active',
          profileData: data.profileData,
          trackingToken: data.trackingToken,
          agencyId: data.agencyId,
          agencyAddress: data.agencyAddress,
          agencyCity: data.agencyCity,
          agencyPostalCode: data.agencyPostalCode,
          agencySiren: data.agencySiren,
          agencyLatitude: data.agencyLatitude,
          agencyLongitude: data.agencyLongitude,
          agencyCoverageAreas: data.agencyCoverageAreas || [],
          commissionRatePerLead: data.commissionRatePerLead,
        });
      });
      
      console.log('✅ Successfully processed users:', users.length);
      return users;
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      console.error('❌ Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        name: error instanceof Error ? error.name : 'Unknown error type'
      });
      throw error;
    }
  }

  /**
   * Get all referrals/leads
   */
  static async getReferrals(): Promise<Referral[]> {
    try {
      const referralsSnapshot = await getDocs(collection(db, DataService.REFERRALS_COLLECTION));
      const referrals: Referral[] = [];
      
      referralsSnapshot.forEach((doc) => {
        const data = doc.data();
        referrals.push({
          id: doc.id,
          recommenderId: data.recommenderId || '',
          agencyId: data.agencyId,
          sellerFirstName: data.sellerFirstName || '',
          sellerLastName: data.sellerLastName || '',
          sellerPhone: data.sellerPhone || '',
          sellerEmail: data.sellerEmail || '',
          propertyAddress: data.propertyAddress || '',
          propertyType: data.propertyType || '',
          propertyDescription: data.propertyDescription,
          status: data.status || 'pending',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate(),
          gdprConsent: data.gdprConsent || false,
          trackingToken: data.trackingToken || '',
          notes: data.notes,
          commissionAmount: data.commissionAmount,
          soldDate: data.soldDate?.toDate(),
          soldPrice: data.soldPrice || 0,
          images: data.images || [],
        });
      });
      
      return referrals;
    } catch (error) {
      console.error('Error fetching referrals:', error);
      throw error;
    }
  }

  /**
   * Get referrals by status for pipeline view
   */
  static async getReferralsByStatus(status: string): Promise<Referral[]> {
    try {
      const q = query(
        collection(db, DataService.REFERRALS_COLLECTION),
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const referrals: Referral[] = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        referrals.push({
          id: doc.id,
          recommenderId: data.recommenderId || '',
          agencyId: data.agencyId,
          sellerFirstName: data.sellerFirstName || '',
          sellerLastName: data.sellerLastName || '',
          sellerPhone: data.sellerPhone || '',
          sellerEmail: data.sellerEmail || '',
          propertyAddress: data.propertyAddress || '',
          propertyType: data.propertyType || '',
          propertyDescription: data.propertyDescription,
          status: data.status || 'pending',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate(),
          gdprConsent: data.gdprConsent || false,
          trackingToken: data.trackingToken || '',
          notes: data.notes,
          commissionAmount: data.commissionAmount,
          soldDate: data.soldDate?.toDate(),
          soldPrice: data.soldPrice || 0,
          images: data.images || [],
        });
      });
      
      return referrals;
    } catch (error) {
      console.error('Error fetching referrals by status:', error);
      throw error;
    }
  }

  /**
   * Get KPI data calculated from real data
   */
  static async getKPIData(): Promise<KPIData> {
    try {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      // Get all referrals for date filtering
      const referrals = await DataService.getReferrals();
      
      // Calculate day/week/month leads
      const dayLeads = referrals.filter(r => r.createdAt >= startOfDay).length;
      const weekLeads = referrals.filter(r => r.createdAt >= startOfWeek).length;
      const monthLeads = referrals.filter(r => r.createdAt >= startOfMonth).length;

      // Calculate signed mandates (referrals with agency assigned)
      const dayMandates = referrals.filter(r => r.agencyId && r.createdAt >= startOfDay).length;
      const weekMandates = referrals.filter(r => r.agencyId && r.createdAt >= startOfWeek).length;
      const monthMandates = referrals.filter(r => r.agencyId && r.createdAt >= startOfMonth).length;

      // Calculate conversion rate
      const totalReferrals = referrals.length;
      const soldReferrals = referrals.filter(r => r.status === 'sold').length;
      const conversionRate = totalReferrals > 0 ? (soldReferrals / totalReferrals) * 100 : 0;

      // Calculate commissions
      const pendingCommissions = referrals
        .filter(r => r.status === 'sold' && r.commissionAmount)
        .reduce((sum, r) => sum + (r.commissionAmount || 0), 0);

      const validatedCommissions = referrals
        .filter(r => r.status === 'sold' && r.commissionAmount)
        .reduce((sum, r) => sum + (r.commissionAmount || 0), 0);

      return {
        leadsGenerated: {
          day: dayLeads,
          week: weekLeads,
          month: monthLeads,
        },
        signedMandates: {
          day: dayMandates,
          week: weekMandates,
          month: monthMandates,
        },
        conversionRate: Math.round(conversionRate * 100) / 100,
        commissionsToPay: pendingCommissions,
        commissionsValidated: validatedCommissions,
      };
    } catch (error) {
      console.error('Error calculating KPI data:', error);
      throw error;
    }
  }

  /**
   * Get chart data calculated from real data
   */
  static async getChartData(): Promise<ChartData> {
    try {
      const referrals = await DataService.getReferrals();
      const users = await DataService.getUsers();

      // Calculate leads per month (last 12 months)
      const leadsPerMonth: Array<{ month: string; count: number }> = [];
      const now = new Date();
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleDateString('fr-FR', { month: 'long' });
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        const count = referrals.filter(r => 
          r.createdAt >= monthStart && r.createdAt <= monthEnd
        ).length;
        
        leadsPerMonth.push({ month: monthName, count });
      }

      // Calculate conversion funnel
      const totalLeads = referrals.length;
      const pendingLeads = referrals.filter(r => r.status === 'pending').length;
      const contactedLeads = referrals.filter(r => r.status === 'contacted').length;
      const meetingLeads = referrals.filter(r => r.status === 'meeting').length;
      const soldLeads = referrals.filter(r => r.status === 'sold').length;
      const lostLeads = referrals.filter(r => r.status === 'lost').length;
      const expiredLeads = referrals.filter(r => r.status === 'expired').length;

      // Only show stages that have data, and combine some stages for better visualization
      const conversionFunnel = [];
      
      if (totalLeads > 0) {
        conversionFunnel.push({ stage: 'Total Leads', count: totalLeads, color: '#2196F3' });
      }
      
      if (pendingLeads > 0) {
        conversionFunnel.push({ stage: 'En attente', count: pendingLeads, color: '#FF9800' });
      }
      
      if (contactedLeads > 0) {
        conversionFunnel.push({ stage: 'Contacté', count: contactedLeads, color: '#4CAF50' });
      }
      
      if (meetingLeads > 0) {
        conversionFunnel.push({ stage: 'Rendez-vous', count: meetingLeads, color: '#FF5722' });
      }
      
      if (soldLeads > 0) {
        conversionFunnel.push({ stage: 'Vendu', count: soldLeads, color: '#9C27B0' });
      }
      
      if (lostLeads > 0) {
        conversionFunnel.push({ stage: 'Perdu', count: lostLeads, color: '#F44336' });
      }
      
      if (expiredLeads > 0) {
        conversionFunnel.push({ stage: 'Expiré', count: expiredLeads, color: '#607D8B' });
      }

      // If no data, show a placeholder
      if (conversionFunnel.length === 0) {
        conversionFunnel.push({ stage: 'Aucune donnée', count: 0, color: '#E0E0E0' });
      }

      // Calculate top sponsors (recommenders)
      const recommenderStats = new Map<string, { leads: number; sold: number }>();
      
      referrals.forEach(r => {
        const current = recommenderStats.get(r.recommenderId) || { leads: 0, sold: 0 };
        current.leads++;
        if (r.status === 'sold') current.sold++;
        recommenderStats.set(r.recommenderId, current);
      });

      const topSponsors = Array.from(recommenderStats.entries())
        .map(([id, stats]) => {
          const user = users.find(u => u.id === id);
          return {
            name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            leads: stats.leads,
            conversion: stats.leads > 0 ? (stats.sold / stats.leads) * 100 : 0,
          };
        })
        .sort((a, b) => b.leads - a.leads)
        .slice(0, 5);

      // Calculate top agencies
      const agencyStats = new Map<string, { leads: number; mandates: number }>();
      
      referrals.forEach(r => {
        if (r.agencyId) {
          const current = agencyStats.get(r.agencyId) || { leads: 0, mandates: 0 };
          current.leads++;
          if (r.status !== 'pending') current.mandates++;
          agencyStats.set(r.agencyId, current);
        }
      });

      const topAgencies = Array.from(agencyStats.entries())
        .map(([id, stats]) => {
          const user = users.find(u => u.id === id);
          return {
            name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
            leads: stats.leads,
            mandates: stats.mandates,
          };
        })
        .sort((a, b) => b.leads - a.leads)
        .slice(0, 5);

      return {
        leadsPerMonth,
        conversionFunnel,
        topSponsors,
        topAgencies,
      };
    } catch (error) {
      console.error('Error calculating chart data:', error);
      throw error;
    }
  }

  /**
   * Get geographic data for analytics
   */
  static async getGeographicData(): Promise<GeographicData[]> {
    try {
      const users = await DataService.getUsers();
      const referrals = await DataService.getReferrals();
      
      // Group referrals by agency city/region
      const cityStats = new Map<string, {
        leads: number;
        conversions: number;
        revenue: number;
        agencies: Set<string>;
      }>();
      
      referrals.forEach(referral => {
        if (referral.agencyId) {
          const agency = users.find(u => u.id === referral.agencyId);
          if (agency && agency.agencyCity) {
            const city = agency.agencyCity;
            const current = cityStats.get(city) || {
              leads: 0,
              conversions: 0,
              revenue: 0,
              agencies: new Set<string>()
            };
            
            current.leads++;
            current.agencies.add(agency.id);
            
            if (referral.status === 'sold') {
              current.conversions++;
              current.revenue += referral.soldPrice || 0;
            }
            
            cityStats.set(city, current);
          }
        }
      });
      
      // Convert to array format
      const geographicData: GeographicData[] = Array.from(cityStats.entries()).map(([city, stats]) => ({
        region: city,
        leads: stats.leads,
        conversions: stats.conversions,
        revenue: stats.revenue,
        agencies: stats.agencies.size,
        conversionRate: stats.leads > 0 ? (stats.conversions / stats.leads) * 100 : 0,
      }));
      
      // Sort by leads (descending)
      return geographicData.sort((a, b) => b.leads - a.leads);
    } catch (error) {
      console.error('Error calculating geographic data:', error);
      throw error;
    }
  }

  /**
   * Get commissions calculated from referrals
   */
  static async getCommissions(): Promise<Commission[]> {
    try {
      const referrals = await DataService.getReferrals();
      const users = await DataService.getUsers();
      
      const commissions: Commission[] = [];
      
      // Convert referrals to commissions
      referrals.forEach(referral => {
        if (referral.commissionAmount && referral.commissionAmount > 0) {
          const recommender = users.find(u => u.id === referral.recommenderId);
          const agency = users.find(u => u.id === referral.agencyId);
          
          if (recommender && agency) {
            commissions.push({
              id: referral.id,
              referralId: referral.id,
              recommenderId: referral.recommenderId,
              recommenderName: `${recommender.firstName} ${recommender.lastName}`,
              agencyId: referral.agencyId || '',
              agencyName: `${agency.firstName} ${agency.lastName}`,
              amount: referral.commissionAmount,
              status: referral.status === 'sold' ? CommissionStatus.VALIDATED : 
                     referral.status === 'pending' ? CommissionStatus.PENDING : CommissionStatus.PENDING,
              createdAt: referral.createdAt,
              paidAt: referral.status === 'sold' ? referral.updatedAt : undefined,
              paymentMethod: referral.status === 'sold' ? 'Commission automatique' : undefined,
            });
          }
        }
      });
      
      return commissions;
    } catch (error) {
      console.error('Error calculating commissions:', error);
      throw error;
    }
  }

  /**
   * Get notifications for the admin panel
   */
  static async getNotifications(): Promise<Array<{
    id: string;
    type: 'info' | 'warning' | 'error' | 'success';
    title: string;
    message: string;
    timestamp: Date;
    read: boolean;
    actionUrl?: string;
  }>> {
    try {
      const referrals = await DataService.getReferrals();
      const users = await DataService.getUsers();
      const notifications = [];

      // Generate notifications based on real data
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // New leads notifications (last 24 hours)
      const recentLeads = referrals.filter(r => r.createdAt >= oneDayAgo);
      if (recentLeads.length > 0) {
        notifications.push({
          id: 'recent-leads',
          type: 'info' as const,
          title: 'Nouveaux leads',
          message: `${recentLeads.length} nouveau(x) lead(s) reçu(s) dans les dernières 24h`,
          timestamp: new Date(),
          read: false,
          actionUrl: '/leads',
        });
      }

      // High-value conversions (last week)
      const highValueSales = referrals.filter(r => 
        r.status === 'sold' && 
        r.createdAt >= oneWeekAgo && 
        (r.soldPrice || 0) > 500000
      );
      if (highValueSales.length > 0) {
        notifications.push({
          id: 'high-value-sales',
          type: 'success' as const,
          title: 'Ventes à haute valeur',
          message: `${highValueSales.length} vente(s) de plus de 500k€ cette semaine`,
          timestamp: new Date(),
          read: false,
          actionUrl: '/commissions',
        });
      }

      // Suspended users notifications
      const suspendedUsers = users.filter(u => !u.isActive);
      if (suspendedUsers.length > 0) {
        notifications.push({
          id: 'suspended-users',
          type: 'warning' as const,
          title: 'Utilisateurs suspendus',
          message: `${suspendedUsers.length} utilisateur(s) actuellement suspendu(s)`,
          timestamp: new Date(),
          read: false,
          actionUrl: '/users',
        });
      }

      // Low conversion rate warning
      const totalLeads = referrals.length;
      const soldLeads = referrals.filter(r => r.status === 'sold').length;
      const conversionRate = totalLeads > 0 ? (soldLeads / totalLeads) * 100 : 0;
      
      if (totalLeads > 10 && conversionRate < 10) {
        notifications.push({
          id: 'low-conversion',
          type: 'warning' as const,
          title: 'Taux de conversion faible',
          message: `Taux de conversion actuel: ${conversionRate.toFixed(1)}% (objectif: >10%)`,
          timestamp: new Date(),
          read: false,
          actionUrl: '/analytics',
        });
      }

      // Pending commissions
      const pendingCommissions = referrals.filter(r => 
        r.status === 'sold' && r.commissionAmount && r.commissionAmount > 0
      );
      if (pendingCommissions.length > 0) {
        const totalAmount = pendingCommissions.reduce((sum, r) => sum + (r.commissionAmount || 0), 0);
        notifications.push({
          id: 'pending-commissions',
          type: 'info' as const,
          title: 'Commissions en attente',
          message: `${pendingCommissions.length} commission(s) en attente pour un total de ${totalAmount.toFixed(0)}€`,
          timestamp: new Date(),
          read: false,
          actionUrl: '/commissions',
        });
      }

      // Sort by timestamp (newest first)
      return notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  /**
   * Get sponsor codes (tracking tokens) for users
   */
  static async getSponsorCodes(): Promise<Array<{
    id: string;
    code: string;
    sponsorName: string;
    usageCount: number;
    status: 'active' | 'inactive';
    commissionRate: number;
    createdAt: Date;
    lastUsed?: Date;
  }>> {
    try {
      const users = await DataService.getUsers();
      const referrals = await DataService.getReferrals();
      
      return users
        .filter(user => user.role === 'recommender' && user.trackingToken)
        .map(user => {
          const userReferrals = referrals.filter(r => r.recommenderId === user.id);
          const usageCount = userReferrals.length;
          const lastUsed = userReferrals.length > 0 
            ? userReferrals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0].createdAt
            : undefined;
          
          return {
            id: user.id,
            code: user.trackingToken!,
            sponsorName: `${user.firstName} ${user.lastName}`,
            usageCount,
            status: user.isActive ? 'active' : 'inactive',
            commissionRate: user.commissionRatePerLead || 0,
            createdAt: user.createdAt,
            lastUsed,
          };
        });
    } catch (error) {
      console.error('Error fetching sponsor codes:', error);
      throw error;
    }
  }

  /**
   * Get QR codes for tracking
   */
  static async getQRCodes(): Promise<Array<{
    id: string;
    name: string;
    url: string;
    scanCount: number;
    status: 'active' | 'inactive';
    createdAt: Date;
    lastScanned?: Date;
  }>> {
    try {
      // For now, generate QR codes based on tracking tokens
      // In a real implementation, you'd store QR code data in Firestore
      const users = await DataService.getUsers();
      const referrals = await DataService.getReferrals();
      
      return users
        .filter(user => user.role === 'recommender' && user.trackingToken)
        .map(user => {
          const userReferrals = referrals.filter(r => r.recommenderId === user.id);
          const scanCount = userReferrals.length;
          const lastScanned = userReferrals.length > 0 
            ? userReferrals.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0].createdAt
            : undefined;
          
          return {
            id: user.id,
            name: `QR ${user.firstName} ${user.lastName}`,
            url: `https://parrainageimmo-ae141.web.app/refer?token=${user.trackingToken}`,
            scanCount,
            status: user.isActive ? 'active' : 'inactive',
            createdAt: user.createdAt,
            lastScanned,
          };
        });
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      throw error;
    }
  }

  /**
   * Get system configuration
   */
  static async getSystemConfig(): Promise<{
    commissionType: 'fixed' | 'percentage';
    commissionRate: number;
    bonusPerLead: number;
    monthlyBonus: number;
    autoValidation: boolean;
    notifications: boolean;
  }> {
    try {
      // For now, return default values
      // In a real implementation, you'd store this in Firestore
      return {
        commissionType: 'percentage',
        commissionRate: 5,
        bonusPerLead: 50,
        monthlyBonus: 200,
        autoValidation: true,
        notifications: true,
      };
    } catch (error) {
      console.error('Error fetching system config:', error);
      throw error;
    }
  }

  /**
   * Update system configuration
   */
  static async updateSystemConfig(config: {
    commissionType: 'fixed' | 'percentage';
    commissionRate: number;
    bonusPerLead: number;
    monthlyBonus: number;
    autoValidation: boolean;
    notifications: boolean;
  }): Promise<void> {
    try {
      // TODO: Implement saving to Firestore
      console.log('Saving system config:', config);
      // await setDoc(doc(db, 'system_config', 'main'), config);
    } catch (error) {
      console.error('Error updating system config:', error);
      throw error;
    }
  }
}
